import { useSyncExternalStore } from 'react';

/**
 * Calidad gráfica (Ajustes): automática o fija.
 *  - Alta  (nivel 0): sombras suaves 2048, resolución hasta 1,5× (celular) / 2× (PC), todos los efectos.
 *  - Media (nivel 1): sombras 1024, resolución 1×, menos partículas.
 *  - Baja  (nivel 2): sin sombras, resolución 0,75×, menos público, bullpen y banca con menos animación, partículas mínimas.
 * En automática empieza en Alta (Media en equipos con pocos núcleos o poca
 * memoria) y baja un nivel si los FPS se mantienen por debajo de 30.
 */
export type GraphicsPref = 'auto' | 'alta' | 'media' | 'baja';
export const GRAPHICS_LEVEL: Record<Exclude<GraphicsPref, 'auto'>, number> = { alta: 0, media: 1, baja: 2 };
const KEY = 'sportsarena.graphics';

let pref: GraphicsPref = (() => {
  try {
    const v = typeof window !== 'undefined' ? localStorage.getItem(KEY) : null;
    return v === 'alta' || v === 'media' || v === 'baja' ? v : 'auto';
  } catch {
    return 'auto';
  }
})();
const listeners = new Set<() => void>();
export const getGraphicsPref = (): GraphicsPref => pref;
export const setGraphicsPref = (v: GraphicsPref): void => {
  pref = v;
  try {
    localStorage.setItem(KEY, v);
  } catch {
    /* sin almacenamiento */
  }
  listeners.forEach((l) => l());
};
export const useGraphicsPref = (): GraphicsPref =>
  useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getGraphicsPref,
    getGraphicsPref
  );

/** Nivel inicial en automática: Media en equipos modestos (≤ 4 núcleos o ≤ 4 GB). */
export const autoStartLevel = (): number => {
  try {
    const nav = navigator as Navigator & { deviceMemory?: number };
    if ((nav.hardwareConcurrency ?? 8) <= 4 || (nav.deviceMemory ?? 8) <= 4) return 1;
  } catch {
    /* sin datos */
  }
  return 0;
};

/** Nivel efectivo actual (lo fija la página del partido; lo leen los efectos en cada frame). */
let level = 0;
export const graphicsLevel = (): number => level;
export const setGraphicsLevel = (v: number): void => {
  level = v;
};
